// The one dropdown skin for v2 forms: a trigger styled like our text
// inputs, a real popover listbox with keyboard navigation and a check on
// the selected row — the interaction of after-internal's select, on
// react-aria so the accessibility comes for free.

import React, { useContext } from "react";
import {
  Button,
  ListBox,
  ListBoxItem,
  Popover,
  Select,
  SelectValue,
} from "react-aria-components";
import { CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

import { FormReadOnlyContext } from "../read-only-context";

export interface SelectInputOption {
  value: string;
  label: string;
}

interface SelectInputProps {
  id?: string;
  ariaLabel: string;
  options: SelectInputOption[];
  value: string | null;
  placeholder?: string;
  invalid?: boolean;
  onChange: (value: string | null) => void;
  onBlur?: () => void;
  /** Adds a leading row that clears the selection. */
  clearLabel?: string;
}

const CLEAR_KEY = "__clear__";

export default function SelectInput({
  id,
  ariaLabel,
  options,
  value,
  placeholder,
  invalid,
  onChange,
  onBlur,
  clearLabel,
}: SelectInputProps) {
  // react-aria presses run on pointer events, which fire even inside a
  // disabled fieldset — record mode must disable this control by hand.
  const readOnly = useContext(FormReadOnlyContext);
  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      isDisabled={readOnly}
      selectedKey={value ?? null}
      placeholder={placeholder ?? "Select…"}
      onSelectionChange={(key) => {
        onChange(key === CLEAR_KEY ? null : String(key));
      }}
      onBlur={onBlur}
    >
      <Button
        className={`flex h-12 w-full items-center justify-between gap-2 rounded-md border bg-white px-3.5 text-left text-base text-soil transition-colors focus:outline-none focus-visible:border-cantelope-500 focus-visible:ring-2 focus-visible:ring-cantelope/50 ${
          invalid ? "border-red-400" : "border-oat-200"
        }`}
      >
        <SelectValue
          className={({ isPlaceholder }) =>
            `truncate ${isPlaceholder ? "text-oat-600" : ""}`
          }
        />
        <ChevronDownIcon
          aria-hidden="true"
          className="h-5 w-5 shrink-0 text-oat-700"
        />
      </Button>
      <Popover
        offset={4}
        placement="bottom start"
        shouldFlip={false}
        // react-aria sets an inline max-height from the available space,
        // which overrides any class — the cap must come in as a prop.
        maxHeight={224}
        className="w-[var(--trigger-width)] overflow-auto rounded-lg border border-oat-200 bg-white py-1 shadow-lg"
      >
        <ListBox className="outline-none">
          {clearLabel && value && (
            <ListBoxItem
              id={CLEAR_KEY}
              textValue={clearLabel}
              className="cursor-pointer px-4 py-2.5 italic text-oat-700 outline-none data-[focused]:bg-oat-50"
            >
              {clearLabel}
            </ListBoxItem>
          )}
          {options.map((option) => (
            <ListBoxItem
              key={option.value}
              id={option.value}
              textValue={option.label}
              className="flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 text-soil outline-none data-[focused]:bg-oat-50 data-[selected]:font-bold"
            >
              {({ isSelected }) => (
                <>
                  <span className="truncate">{option.label}</span>
                  {isSelected && (
                    <CheckIcon
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0 text-evergreen"
                    />
                  )}
                </>
              )}
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </Select>
  );
}
